import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import type {
    ChartAccount,
    LocaleCode,
    OperationCategory,
    OperationTemplateDetail,
    UserRole,
} from '@/api/models';
import type { ErrorResponse } from '@/api/handlers/responses';
import {
    useActivateOperationVersionMutation,
    useChartOfAccounts,
    useCreateOperationTemplateMutation,
    useCurrentTaxSettingsQuery,
    useOperationCategoriesQuery,
    useOperationTemplateQuery,
    useSaveOperationDraftMutation,
    useSetOperationBlockedMutation,
} from '@/api/queries';
import {
    type AccountLabel,
    useAccountLabelMap,
} from '@/pages/accounting/books/journal/components/journalEntryLines';
import {
    resolveLocale,
    todayIso,
    unwrapStatement,
} from '@/pages/accounting/statements/statementMeta';
import { PATH } from '@/router/PATH';
import { parseGelToTetri } from '@/utils/money';
import { getAuthMethod, getUser } from '@/utils/authSession';
import {
    canEditOperations,
    canReadOperations,
    deriveOperationsAccess,
    type OperationsAccess,
} from '../../operationsAccess';
import {
    addPair,
    DEFAULT_SAMPLE_GEL,
    initialPairs,
    nextVersionNumber,
    postableAccounts,
    removePair,
    setLine,
    setLineKind,
    setPairRole,
    subtitleClauses,
    subtitleParts,
    toEditorError,
    toLines,
    versionChip,
    type DraftPair,
    type EditorError,
    type EditorVersionChip,
    type OpenLine,
    type PendingDrop,
    type SubtitleClause,
} from './operationEditorMeta';
import { toPendingDrop } from './pairBoxMeta';
import { previewOperation, type PreviewResult } from './operationPreview';
import type {
    OperationLineDraft,
    OperationLineSide,
    OperationPairRole,
} from '@/api/models';

export type UseOperationEditorPageResult = {
    key: string;
    /**
     * `'create'` on the route with no `:key` at all — nothing has been
     * fetched, there is no version to publish or block yet, and the primary
     * action mints the template instead of saving a draft.
     */
    mode: 'edit' | 'create';
    access: OperationsAccess;
    canRead: boolean;
    locale: LocaleCode;
    /** Whether this session may change anything. A BranchAdmin reads it read-only. */
    canWrite: boolean;
    isLoading: boolean;
    error: ErrorResponse | null;
    onRetry: () => void;
    template: OperationTemplateDetail | null;
    /** Editable in both modes — see the header's rename fields. */
    /** The tenant's category set, in display order. Empty while it loads. */
    categories: OperationCategory[];
    /** The category the template is filed under, as the picker's selection. */
    categoryId: string;
    onCategoryChange: (categoryId: string) => void;
    nameKa: string;
    nameEn: string;
    onNameKaChange: (value: string) => void;
    onNameEnChange: (value: string) => void;
    pairs: DraftPair[];
    roles: UserRole[];
    effectiveFrom: string;
    /** Postable, unarchived leaves — what a fixed line may point at. */
    accounts: ChartAccount[];
    /** The whole chart — group headers and leaves — what a slot's filter picker offers. */
    chartAccounts: ChartAccount[];
    /** Account id → code + localized name, in both languages, for the slot label auto-suggestion. */
    accountLabels: {
        ka: Map<string, AccountLabel>;
        en: Map<string, AccountLabel>;
    };
    sampleGel: string;
    /** The VAT rate the preview applies, in basis points. Zero when none is in force. */
    vatRateBps: number;
    preview: PreviewResult;
    /** The version a publish from here would create. */
    nextVersion: number;
    /** What the navbar chip says about the version on screen. */
    versionChip: EditorVersionChip;
    /** The subtitle's clauses, unknown facts already dropped. */
    subtitle: SubtitleClause[];
    /** How many pairs the version in force has — decides "remove" vs "drop". */
    publishedPairCount: number;
    /** The one account box open for editing, across the whole recipe. */
    openLine: OpenLine | null;
    onOpenLine: (line: OpenLine | null) => void;
    /** The pair a drop dialog is asking about, or `null` when none is open. */
    pendingDrop: PendingDrop | null;
    onRequestDropPair: (pairGroup: number) => void;
    onCancelDropPair: () => void;
    onConfirmDropPair: () => void;
    onPairRoleChange: (pairGroup: number, role: OperationPairRole) => void;
    /** True while an unsaved edit is on screen. */
    isDirty: boolean;
    isSaving: boolean;
    isPublishing: boolean;
    isBlocking: boolean;
    /** The last refusal, anchored to the part of the form that caused it. */
    formError: EditorError | null;
    onSampleChange: (value: string) => void;
    onEffectiveFromChange: (value: string) => void;
    onRolesChange: (roles: UserRole[]) => void;
    onLineChange: (
        pairGroup: number,
        side: OperationLineSide,
        patch: Partial<OperationLineDraft>,
    ) => void;
    onLineKindChange: (
        pairGroup: number,
        side: OperationLineSide,
        kind: 'fixed' | 'slot',
    ) => void;
    onAddPair: () => void;
    onSaveDraft: () => void;
    onPublish: () => void;
    onSetBlocked: (blocked: boolean) => void;
    /** Open this template's version history, in a drawer over the editor. */
    onOpenHistory: () => void;
    /**
     * The key the history drawer is showing, `null` while it is closed.
     *
     * Local state rather than a URL parameter, unlike the catalog's: the editor
     * is a form, and a history stack that fills with a drawer's open and closed
     * states turns the back button — the gesture that means "leave this form" —
     * into something a reader has to press repeatedly.
     */
    historyKey: string | null;
    onCloseHistory: () => void;
    /** `mode === 'create'` only: mint the template, then navigate to its editor. */
    onCreate: () => void;
    isCreating: boolean;
};

/**
 * Drives the template editor.
 *
 * Three reads and three writes. The reads are the template itself, the chart
 * (for the account picker and to resolve a fixed line's id into a code and a
 * name), and the tax rate set in force — the preview needs a real rate, because
 * writing 18 into a client would be exactly the mistake the `VAT_OF_GROSS`
 * source exists to avoid.
 *
 * **Saving and publishing are deliberately different acts.** A draft changes
 * nothing anyone records, so Save is cheap and unguarded. Publishing freezes a
 * version that entries will post under and closes the previous one's
 * effectivity, so it is confirmed, and the screen says what it will do before it
 * does it.
 */
export const useOperationEditorPage = (): UseOperationEditorPageResult => {
    const { key = '' } = useParams<{ key: string }>();
    // The NEW route carries no `:key` param at all, so this is the one
    // reliable signal: an EDIT url always has a real, encoded key.
    const isCreate = key.length === 0;
    const navigate = useNavigate();
    const { i18n } = useTranslation('pages');
    const locale = resolveLocale(i18n.language);

    const access = React.useMemo(
        () => deriveOperationsAccess(getUser(), getAuthMethod()),
        [],
    );
    const canRead = canReadOperations(access);
    const canWrite = canEditOperations(access);

    // Self-disables for an empty key, so this is inert (and never loading)
    // on the create route without needing its own `enabled` branch here.
    const query = useOperationTemplateQuery(key, canRead);
    const { data: template, error } = unwrapStatement(query.data, query.error);

    const chart = useChartOfAccounts({ locale, enabled: canRead });
    const taxes = useCurrentTaxSettingsQuery(canRead);
    // Both languages, for the slot label auto-suggestion — whichever matches
    // `locale` shares its cache entry with `chart` above, so this is at most
    // one net-new request.
    const labelsKa = useAccountLabelMap('ka', canRead);
    const labelsEn = useAccountLabelMap('en', canRead);
    const accountLabels = React.useMemo(
        () => ({ ka: labelsKa, en: labelsEn }),
        [labelsKa, labelsEn],
    );

    // A tenant with no rate set in force is a documented outcome, not a
    // failure — the endpoint 404s. Zero is the right rate for them: NET then
    // equals GROSS and a VAT line previews nil, which is what their books
    // actually do.
    const vatRateBps = taxes.data?.success
        ? taxes.data.response.rates.vatStandardBps
        : 0;

    // Create mode seeds its own blank state once, up front — the template
    // that would normally drive the seeding effect below never arrives,
    // because there is no key yet to fetch one for.
    const [pairs, setPairs] = React.useState<DraftPair[]>(() =>
        isCreate ? initialPairs(null, null) : [],
    );
    const [roles, setRoles] = React.useState<UserRole[]>([]);
    const [effectiveFrom, setEffectiveFrom] = React.useState(() =>
        isCreate ? todayIso() : '',
    );
    const [categoryId, setCategoryId] = React.useState('');
    const [nameKa, setNameKa] = React.useState('');
    const [nameEn, setNameEn] = React.useState('');
    const [sampleGel, setSampleGel] = React.useState(DEFAULT_SAMPLE_GEL);
    const [isDirty, setIsDirty] = React.useState(false);
    const [openLine, setOpenLine] = React.useState<OpenLine | null>(null);
    const [pendingDrop, setPendingDrop] = React.useState<PendingDrop | null>(
        null,
    );
    const [formError, setFormError] = React.useState<EditorError | null>(null);

    // Seed the form once the template lands, and again whenever the server hands
    // back a new one — a save or a publish returns the template as it now
    // stands, so this is what makes the screen agree with what was just written.
    // Keyed on the version ids rather than on object identity, so an incidental
    // refetch that changed nothing does not throw away an in-progress edit.
    const seededFrom = React.useRef<string | null>(null);
    const seedKey = template
        ? `${template.draftVersion?.id ?? '-'}:${template.activeVersion?.id ?? '-'}:${template.status}`
        : null;

    React.useEffect(() => {
        if (!template || !seedKey || seededFrom.current === seedKey) {
            return;
        }
        seededFrom.current = seedKey;
        setPairs(initialPairs(template.draftVersion, template.activeVersion));
        setRoles(template.rolesAllowed);
        setEffectiveFrom(template.draftVersion?.effectiveFrom ?? todayIso());
        setCategoryId(template.categoryId);
        setNameKa(template.nameKa);
        setNameEn(template.nameEn);
        setIsDirty(false);
        // A reseed replaces every pair, so an open box and a half-asked
        // question are both about lines that no longer exist.
        setOpenLine(null);
        setPendingDrop(null);
    }, [template, seedKey]);

    const accounts = React.useMemo(
        () => postableAccounts(chart.data ?? []),
        [chart.data],
    );
    // Unfiltered, unlike `accounts` above: the slot filter picker offers
    // group headers too, since a group's own code is a valid prefix.
    const chartAccounts = chart.data ?? [];

    // A sample the field cannot parse previews as nothing rather than as zero:
    // the amount is what the whole panel is a function of, and rendering a
    // journal of zeros under a half-typed figure reads as a recipe that posts
    // nothing.
    const sampleTetri = React.useMemo(() => {
        const parsed = parseGelToTetri(sampleGel);
        return parsed.ok ? parsed.tetri : 0;
    }, [sampleGel]);

    const draftLines = React.useMemo(() => toLines(pairs), [pairs]);

    const preview = React.useMemo(
        () => previewOperation(draftLines, sampleTetri, vatRateBps),
        [draftLines, sampleTetri, vatRateBps],
    );

    // The header's three derivations. All are functions of what is already on
    // screen — the draft's pairs, not the server's summary — so they move with
    // an edit rather than with a save.
    const nextVersion = React.useMemo(
        () => nextVersionNumber(template),
        [template],
    );
    const chip = React.useMemo(() => versionChip(template), [template]);
    // Pairs in the version in force. Counted off its lines rather than read
    // from `pairCount`, so a version whose lines and summary disagree reports
    // what it actually contains.
    const publishedPairCount = React.useMemo(
        () =>
            template?.activeVersion
                ? new Set(
                      template.activeVersion.lines.map(
                          (line) => line.pairGroup,
                      ),
                  ).size
                : 0,
        [template],
    );
    const subtitle = React.useMemo(
        () => subtitleClauses(subtitleParts(template, pairs, preview.balanced)),
        [pairs, preview.balanced, template],
    );

    // The whole set, not just the one in use: the picker offers a choice, and a
    // failed read leaves it absent rather than empty — a control with no options
    // looks broken, and a category is not what anyone opened this screen for.
    const categoriesQuery = useOperationCategoriesQuery(canRead);

    const createTemplate = useCreateOperationTemplateMutation();
    const saveDraft = useSaveOperationDraftMutation();
    const activate = useActivateOperationVersionMutation();
    const setBlocked = useSetOperationBlockedMutation();

    const touch = React.useCallback(() => {
        setIsDirty(true);
        // Clearing the refusal on the next edit rather than on the next save is
        // what stops a stale "pair 2 is unbalanced" sitting under a pair 2 the
        // author has since fixed.
        setFormError(null);
    }, []);

    const onLineChange = React.useCallback(
        (
            pairGroup: number,
            side: OperationLineSide,
            patch: Partial<OperationLineDraft>,
        ) => {
            setPairs((current) => setLine(current, pairGroup, side, patch));
            touch();
        },
        [touch],
    );

    const onLineKindChange = React.useCallback(
        (
            pairGroup: number,
            side: OperationLineSide,
            kind: 'fixed' | 'slot',
        ) => {
            setPairs((current) => setLineKind(current, pairGroup, side, kind));
            touch();
        },
        [touch],
    );

    const onAddPair = React.useCallback(() => {
        setPairs((current) => addPair(current));
        touch();
    }, [touch]);

    const onPairRoleChange = React.useCallback(
        (pairGroup: number, role: OperationPairRole) => {
            setPairs((current) => setPairRole(current, pairGroup, role));
            touch();
        },
        [touch],
    );

    /**
     * Removing a pair always asks first, and the question depends on whether
     * the pair has ever published — see `toPendingDrop`.
     *
     * The dialog's copy is frozen here rather than read off the draft while it
     * is open, because confirming renumbers every pair below this one and a
     * dialog that re-read the draft mid-close would name the wrong pair.
     */
    const onRequestDropPair = React.useCallback(
        (pairGroup: number) => {
            setPendingDrop(toPendingDrop(pairs, pairGroup, accounts, template));
        },
        [accounts, pairs, template],
    );

    const onCancelDropPair = React.useCallback(() => setPendingDrop(null), []);

    const onConfirmDropPair = React.useCallback(() => {
        if (!pendingDrop) {
            return;
        }
        setPairs((current) => removePair(current, pendingDrop.pairGroup));
        setOpenLine(null);
        setPendingDrop(null);
        touch();
    }, [pendingDrop, touch]);

    const onRolesChange = React.useCallback(
        (next: UserRole[]) => {
            setRoles(next);
            touch();
        },
        [touch],
    );

    const onEffectiveFromChange = React.useCallback(
        (value: string) => {
            setEffectiveFrom(value);
            touch();
        },
        [touch],
    );

    const onNameKaChange = React.useCallback(
        (value: string) => {
            setNameKa(value);
            touch();
        },
        [touch],
    );

    const onNameEnChange = React.useCallback(
        (value: string) => {
            setNameEn(value);
            touch();
        },
        [touch],
    );

    /**
     * Refile the operation. `touch` because it is an unsaved change like a
     * rename — it rides the next draft save rather than posting on its own,
     * which is what keeps the editor's one dirty flag telling the truth.
     */
    const onCategoryChange = React.useCallback(
        (value: string) => {
            setCategoryId(value);
            touch();
        },
        [touch],
    );

    const onCreate = React.useCallback(() => {
        setFormError(null);
        createTemplate.mutate(
            { nameKa, nameEn, categoryId: categoryId || undefined },
            {
                onSuccess: (result) => {
                    if (result.success) {
                        // The create route has nothing to reseed — the next
                        // screen is the ordinary editor for the key the
                        // server just assigned.
                        navigate(
                            PATH.ACCOUNTING.OPERATIONS.BIND_EDIT(
                                result.response.key,
                            ),
                        );
                    } else {
                        setFormError(toEditorError(result.error));
                    }
                },
            },
        );
    }, [categoryId, createTemplate, nameEn, nameKa, navigate]);

    const onSaveDraft = React.useCallback(() => {
        setFormError(null);
        saveDraft.mutate(
            {
                key,
                body: {
                    effectiveFrom,
                    rolesAllowed: roles,
                    lines: draftLines,
                    nameKa,
                    nameEn,
                    categoryId: categoryId || undefined,
                },
            },
            {
                onSuccess: (result) => {
                    if (result.success) {
                        // Let the seeding effect adopt the server's answer: it
                        // carries the ids the server assigned and the pair
                        // renumbering it applied, and a form that kept its own
                        // copy would drift from both.
                        seededFrom.current = null;
                        setIsDirty(false);
                    } else {
                        setFormError(toEditorError(result.error));
                    }
                },
            },
        );
    }, [
        categoryId,
        draftLines,
        effectiveFrom,
        key,
        nameEn,
        nameKa,
        roles,
        saveDraft,
    ]);

    /**
     * Publish what is on screen.
     *
     * Saves first, unconditionally, then activates the version the save
     * returned. Publishing a draft the author has edited since saving would
     * publish a recipe they are not looking at — the one thing this screen must
     * never do — and asking them to press Save first is a rule they would have
     * to learn by getting it wrong once.
     */
    const onPublish = React.useCallback(() => {
        setFormError(null);
        saveDraft.mutate(
            {
                key,
                body: {
                    effectiveFrom,
                    rolesAllowed: roles,
                    lines: draftLines,
                    nameKa,
                    nameEn,
                    categoryId: categoryId || undefined,
                },
            },
            {
                onSuccess: (saved) => {
                    if (!saved.success) {
                        setFormError(toEditorError(saved.error));
                        return;
                    }
                    const version = saved.response.draftVersion?.version;
                    if (version === undefined) {
                        return;
                    }
                    activate.mutate(
                        { key, version, effectiveFrom },
                        {
                            onSuccess: (result) => {
                                if (result.success) {
                                    seededFrom.current = null;
                                    setIsDirty(false);
                                    navigate(
                                        PATH.ACCOUNTING.OPERATIONS.TEMPLATES,
                                    );
                                } else {
                                    setFormError(toEditorError(result.error));
                                }
                            },
                        },
                    );
                },
            },
        );
    }, [
        activate,
        categoryId,
        draftLines,
        effectiveFrom,
        key,
        nameEn,
        nameKa,
        navigate,
        roles,
        saveDraft,
    ]);

    const onSetBlocked = React.useCallback(
        (blocked: boolean) => {
            setFormError(null);
            setBlocked.mutate(
                { key, blocked },
                {
                    onSuccess: (result) => {
                        if (result.success) {
                            seededFrom.current = null;
                        } else {
                            setFormError(toEditorError(result.error));
                        }
                    },
                },
            );
        },
        [key, setBlocked],
    );

    const [isHistoryOpen, setIsHistoryOpen] = React.useState(false);

    const onOpenHistory = React.useCallback(() => {
        setIsHistoryOpen(true);
    }, []);

    const onCloseHistory = React.useCallback(() => {
        setIsHistoryOpen(false);
    }, []);

    const onRetry = React.useCallback(() => {
        query.refetch();
    }, [query]);

    return {
        key,
        mode: isCreate ? 'create' : 'edit',
        access,
        canRead,
        locale,
        canWrite,
        // The chart is part of the screen, not a decoration: without it a fixed
        // line renders as a bare uuid. So the editor is loading until both land.
        // Create mode has no template to wait for — there is no key yet to
        // have fetched one with.
        isLoading:
            canRead &&
            (chart.isLoading ||
                (!isCreate && (query.isLoading || template === null))),
        error,
        onRetry,
        template,
        categories: categoriesQuery.data?.success
            ? categoriesQuery.data.response
            : [],
        categoryId,
        onCategoryChange,
        nameKa,
        nameEn,
        onNameKaChange,
        onNameEnChange,
        pairs,
        roles,
        effectiveFrom,
        accounts,
        chartAccounts,
        accountLabels,
        sampleGel,
        vatRateBps,
        preview,
        nextVersion,
        versionChip: chip,
        subtitle,
        publishedPairCount,
        openLine,
        onOpenLine: setOpenLine,
        pendingDrop,
        onRequestDropPair,
        onCancelDropPair,
        onConfirmDropPair,
        onPairRoleChange,
        isDirty,
        isSaving: saveDraft.isPending,
        isPublishing: activate.isPending || saveDraft.isPending,
        isBlocking: setBlocked.isPending,
        formError,
        onSampleChange: setSampleGel,
        onEffectiveFromChange,
        onRolesChange,
        onLineChange,
        onLineKindChange,
        onAddPair,
        onSaveDraft,
        onPublish,
        onSetBlocked,
        onOpenHistory,
        // Never in create mode: there is no key yet, so there is no history to
        // read — and the toolbar that opens it is not rendered there either.
        historyKey: isHistoryOpen && !isCreate ? key : null,
        onCloseHistory,
        onCreate,
        isCreating: createTemplate.isPending,
    };
};
